import { createId } from '@paralleldrive/cuid2';
import { type RefObject, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import type { SkuAssemblyComponentDTO, SkuDTO } from '@invenco/products-interface';

import { Button } from '@/components/core/button';
import { DialogBody, DialogDescription, DialogHeader, DialogTitle } from '@/components/core/dialog';
import { useDialogContext } from '@/components/dialog-with-context';
import { FormDialogContent } from '@/components/forms/form-dialog';
import { SortableList } from '@/components/sortable-list';
import type { Result } from '@/shared/helpers/Result';

import { SkuBadge } from '../../components/sku-badge';
import { SkuSearchCombobox } from './sku-search-combobox';

type EditableSubstitute = {
  id: string;
  componentSku?: SkuDTO;
};

type Props = {
  components: Partial<SkuAssemblyComponentDTO>[];
  onSubmit: (
    components: Partial<SkuAssemblyComponentDTO>[],
  ) => Promise<Result> | Promise<void> | void;
};

function SubstituteRow({
  row,
  priority,
  excludedSkuIds,
  portalContainerRef,
  onChange,
}: {
  row: EditableSubstitute;
  priority: number;
  excludedSkuIds: string[];
  portalContainerRef: RefObject<HTMLDivElement | null>;
  onChange: (row: EditableSubstitute) => void;
}) {
  const hasSelectedSku = !!row.componentSku;

  return (
    <>
      {hasSelectedSku ? (
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <SkuBadge name={row.componentSku?.name ?? 'Unknown SKU'} type={row.componentSku?.type} />
          {row.componentSku?.description ? (
            <span className="truncate text-sm">{row.componentSku.description}</span>
          ) : null}
        </div>
      ) : (
        <div className="flex-1">
          <SkuSearchCombobox
            excludedSkuIds={excludedSkuIds}
            portalContainerRef={portalContainerRef}
            onSelect={(componentSku) => onChange({ ...row, componentSku })}
          />
        </div>
      )}
      <div className="flex w-14 justify-center">
        <span className="flex size-8 items-center justify-center rounded-md bg-muted text-sm font-semibold">
          {priority}
        </span>
      </div>
    </>
  );
}

export function SubstitutesManagementDialog({ components, onSubmit }: Props) {
  const { isOpen } = useDialogContext();
  const form = useForm();
  const [rows, setRows] = useState<EditableSubstitute[]>([]);
  const portalContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    setRows(
      components
        .slice()
        .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0))
        .map((component) => ({
          id: component.id ?? createId(),
          componentSku: component.componentSku as SkuDTO | undefined,
        })),
    );
  }, [isOpen, components]);

  const hasIncompleteRow = rows.some((row) => !row.componentSku?.id);
  const canSave = rows.length > 0 && !hasIncompleteRow;
  const canAdd = rows.every((row) => !!row.componentSku);

  const excludedSkuIds = rows
    .filter((row) => row.componentSku?.id)
    .map((row) => row.componentSku!.id);

  return (
    <FormDialogContent
      className="sm:max-w-3xl"
      form={form}
      submitLabel="Save"
      onSubmit={() =>
        onSubmit(
          rows.map((row) => ({
            componentSkuId: row.componentSku?.id,
            quantity: 1,
          })),
        )
      }
      isSubmitDisabled={!canSave}
    >
      <DialogHeader>
        <DialogTitle>Manage substitutes</DialogTitle>
        <DialogDescription>
          Add substitute SKUs and drag to reorder priority. The highest-priority substitute with
          available stock will be used for fulfillment.
        </DialogDescription>
      </DialogHeader>
      <div ref={portalContainerRef} className="absolute top-0 left-0 h-0 w-0 overflow-visible" />
      <DialogBody>
        <div className="space-y-2">
          <div className="flex items-center gap-3 px-2 text-xs font-medium tracking-wide text-muted-foreground uppercase">
            <div className="size-9 shrink-0" />
            <div className="flex-1 shrink-0">SKU / Description</div>
            <div className="w-14 shrink-0 text-center">Priority</div>
            <div className="size-9 shrink-0" />
          </div>
          <SortableList
            items={rows}
            onReorder={setRows}
            onDelete={(row) =>
              setRows((currentRows) => currentRows.filter((candidate) => candidate.id !== row.id))
            }
          >
            {(row) => (
              <SubstituteRow
                row={row}
                priority={rows.indexOf(row) + 1}
                excludedSkuIds={excludedSkuIds}
                portalContainerRef={portalContainerRef}
                onChange={(updatedRow) =>
                  setRows((currentRows) =>
                    currentRows.map((candidate) =>
                      candidate.id === row.id ? updatedRow : candidate,
                    ),
                  )
                }
              />
            )}
          </SortableList>
        </div>
        <Button
          type="button"
          variant="outline"
          className="self-start"
          onClick={() => setRows((currentRows) => [...currentRows, { id: createId() }])}
          disabled={!canAdd}
        >
          Add substitute
        </Button>
      </DialogBody>
    </FormDialogContent>
  );
}
